"use client";

import type { AudioTrackingProps } from "@/app/_providers/native-audio/NativeAudioProvider";

import { NativeAudioProvider } from "@/app/_providers/native-audio/NativeAudioProvider";
import clsx from "clsx";
import { useTranslations } from "next-intl";

import { PreviewPlayerButton } from "./PreviewPlayerButton";
import { PreviewPlayerProgressRing } from "./PreviewPlayerProgressRing";
import { usePreviewPlayer } from "./usePreviewPlayer";

interface PreviewPlayerCoverOverlayProps {
  previewId: string;
  src: string;
  trackingProps?: AudioTrackingProps;
  buttonOffsetX?: number;
  hoverEnabled?: boolean;
  inverted?: boolean;
  isDesktopDefaultVisible?: boolean;
  size?: "sm" | "md";
  isInteractive?: boolean;
  isVisible?: boolean;
  onPreviewPlay?: () => void;
  stopPropagation?: boolean;
}

export function PreviewPlayerCoverOverlay({
  previewId,
  src,
  trackingProps,
  buttonOffsetX,
  hoverEnabled = true,
  inverted = false,
  isDesktopDefaultVisible = false,
  size = "md",
  isInteractive = true,
  isVisible,
  onPreviewPlay,
  stopPropagation = false,
}: PreviewPlayerCoverOverlayProps) {
  const t = useTranslations("ClientKeys.Globals");
  const { isPlaying, isLoading, handleTogglePlay, progressRingProps } = usePreviewPlayer(previewId, src, {
    trackingProps,
    onPreviewPlay,
  });

  const resolvedVisible = isVisible ?? isPlaying;
  const showButton = resolvedVisible || isDesktopDefaultVisible || progressRingProps.isActive;

  const handleClick = (e: React.MouseEvent) => {
    if (stopPropagation) {
      e.stopPropagation();
    }
    if (!isInteractive) return;
    handleTogglePlay();
  };

  return (
    <div
      className={clsx(
        "tw-absolute tw-inset-0 tw-z-10 tw-flex tw-items-center tw-justify-center",
        isInteractive && "tw-cursor-pointer",
      )}
      style={buttonOffsetX ? { transform: `translateX(${buttonOffsetX}px)` } : undefined}
    >
      <div
        className={clsx(
          "tw-transition-opacity tw-duration-300",
          showButton ? "tw-opacity-100" : "tw-pointer-events-none tw-opacity-0",
          hoverEnabled && !showButton && "group-hover:tw-pointer-events-auto group-hover:tw-opacity-100",
          !showButton && "focus-within:tw-pointer-events-auto focus-within:tw-opacity-100",
        )}
      >
        <button
          type='button'
          className='tw-group/preview-button tw-cursor-pointer tw-appearance-none tw-rounded-full tw-border-none tw-bg-transparent tw-p-0 tw-outline-none focus-visible:tw-outline-focus'
          onClick={handleClick}
          aria-label={isPlaying ? t("pause") : t("play")}
        >
          <PreviewPlayerButton inverted={inverted} isActive={isPlaying} isLoading={isLoading} size={size}>
            <PreviewPlayerProgressRing {...progressRingProps} inverted={inverted} size={size} />
          </PreviewPlayerButton>
        </button>
      </div>
    </div>
  );
}

export function PreviewPlayerCoverOverlayWithProvider(props: PreviewPlayerCoverOverlayProps) {
  return (
    <NativeAudioProvider>
      <PreviewPlayerCoverOverlay {...props} />
    </NativeAudioProvider>
  );
}
